import { LocalizedDate } from '@/components/Atoms/LocalizedDate/LocalizedDate';
import useTranslation from '@/hooks/useTranslation';
import { differenceInSeconds } from 'date-fns';
import type { Translate } from 'next-translate';
import { memo, useMemo } from 'react';

/**
 * Show dynamically updating time since an event
 */
const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

/**
 * A component that describes the time passed since a given date.
 *
 * This component is wrapped in `memo` due to it being called significantly during scroll on `InfiniteScrollPage`.
 * Both via ClothesLine and PageHandler.
 */
export const TimeAgo = memo(({ className, datetime }: IProps) => {
    const { t, lang } = useTranslation('common');
    const formatter = useMemo(() => new Intl.RelativeTimeFormat(lang), [lang]);
    const relativeSeconds = differenceInSeconds(new Date(datetime), new Date());
    if (-relativeSeconds > DAY * 14 || relativeSeconds > 0) {
        return <LocalizedDate date={datetime} className={className} />;
    }
    return (
        <time className={className} dateTime={datetime} title={datetime}>
            {getString(relativeSeconds, t, formatter)}
        </time>
    );
});

interface IProps {
    className?: string;
    /** DateTime ISO String */
    datetime: string;
}

const getString = (relativeSeconds: number, t: Translate, formatter: Intl.RelativeTimeFormat): string => {
    if (-relativeSeconds >= DAY) {
        const value = Math.ceil(relativeSeconds / DAY);
        return formatter.format(value, 'day');
    } else if (-relativeSeconds >= HOUR) {
        const value = Math.ceil(relativeSeconds / HOUR);
        return formatter.format(value, 'hour');
    } else if (-relativeSeconds >= MINUTE) {
        const value = Math.ceil(relativeSeconds / MINUTE);
        return formatter.format(value, 'minute');
    }
    return t('TimeAgo.now');
};
